import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Message, Reference } from '../types';
import { useAnnouncer } from '../context/AccessibilityContext';
import MessageItem from './MessageItem';
import './MessageList.css';

interface VirtualizedMessageListProps {
  messages: Message[];
  currentUser: string;
  onReferenceClick: (ref: Reference) => void;
  height?: number; // Container height for virtualization
  itemHeight?: number; // Estimated height per message
}

interface VirtualItem {
  index: number;
  start: number;
  end: number;
}

const VirtualizedMessageList: React.FC<VirtualizedMessageListProps> = ({
  messages,
  currentUser,
  onReferenceClick,
  height = 400,
  itemHeight = 80
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollElementRef = useRef<HTMLDivElement>(null);
  const announcer = useAnnouncer();
  const previousMessageCount = useRef(messages.length);
  
  const [scrollTop, setScrollTop] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Group messages by sender and time proximity (within 5 minutes)
  const messageGroups = useMemo(() => {
    const grouped: Array<{ messages: Message[]; showTimestamp: boolean }> = [];
    
    messages.forEach((message, index) => {
      const prevMessage = messages[index - 1];
      const shouldGroup = prevMessage && 
        prevMessage.sender === message.sender &&
        (new Date(message.timestamp).getTime() - new Date(prevMessage.timestamp).getTime()) < 5 * 60 * 1000;

      if (shouldGroup && grouped.length > 0) {
        grouped[grouped.length - 1].messages.push(message);
      } else {
        grouped.push({
          messages: [message],
          showTimestamp: true
        });
      }
    });

    return grouped;
  }, [messages]);

  // Calculate virtual items to render
  const virtualItems = useMemo(() => {
    const containerHeight = height;
    const startIndex = Math.floor(scrollTop / itemHeight);
    const endIndex = Math.min(
      startIndex + Math.ceil(containerHeight / itemHeight) + 1,
      messageGroups.length - 1
    );

    const items: VirtualItem[] = [];
    for (let i = startIndex; i <= endIndex; i++) {
      items.push({
        index: i,
        start: i * itemHeight,
        end: (i + 1) * itemHeight
      });
    }

    return items;
  }, [scrollTop, itemHeight, height, messageGroups.length]);

  // Handle scroll events
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    setScrollTop(scrollTop);
    setIsScrolling(true);

    // Clear existing timeout
    if (scrollingTimeoutRef.current) {
      clearTimeout(scrollingTimeoutRef.current);
    }

    // Set scrolling to false after scroll ends
    scrollingTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 150);
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollElementRef.current && !isScrolling) {
      const shouldAutoScroll = scrollTop + height >= (messageGroups.length * itemHeight) - (itemHeight * 2);
      
      if (shouldAutoScroll) {
        scrollElementRef.current.scrollTop = messageGroups.length * itemHeight;
      }
    }
  }, [messages, height, itemHeight, messageGroups.length, scrollTop, isScrolling]);

  // Announce new messages to screen readers
  useEffect(() => {
    if (messages.length > previousMessageCount.current) {
      const newMessages = messages.slice(previousMessageCount.current);
      newMessages.forEach(message => {
        if (message.sender !== currentUser) {
          announcer.announceNewMessage(message.sender, message.content);
        }
      });
    }
    previousMessageCount.current = messages.length;
  }, [messages, currentUser, announcer]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollingTimeoutRef.current) {
        clearTimeout(scrollingTimeoutRef.current);
      }
    };
  }, []);

  const totalHeight = messageGroups.length * itemHeight;

  return (
    <div className="message-list" ref={containerRef}>
      <div 
        className="message-list__scroll-container"
        ref={scrollElementRef}
        style={{ height, overflowY: 'auto' }}
        onScroll={handleScroll}
        role="log"
        aria-live="polite"
        aria-label="Chat messages"
      >
        <div style={{ height: totalHeight, position: 'relative' }}>
          {virtualItems.map(({ index, start }) => {
            const group = messageGroups[index];
            if (!group) return null;

            return (
              <div
                key={index}
                style={{
                  position: 'absolute',
                  top: start,
                  left: 0,
                  right: 0,
                  minHeight: itemHeight
                }}
                className="message-group"
              >
                {group.messages.map((message, messageIndex) => (
                  <MessageItem
                    key={message.id}
                    message={message}
                    isOwn={message.sender === currentUser}
                    showTimestamp={messageIndex === group.messages.length - 1 && group.showTimestamp}
                    onReferenceClick={onReferenceClick}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default React.memo(VirtualizedMessageList);